import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
} from 'react';

/** One stop of the "On this page" rail: a section and its anchor. */
export interface SettingsRailItem {
  id: string;
  title: string;
}

/** Collects the sections a settings page renders, for its rail. */
interface SettingsRailRegistry {
  register(item: SettingsRailItem): () => void;
}

export const SettingsRailContext = createContext<SettingsRailRegistry | null>(
  null
);

/**
 * Lists a section on the rail of its page while it is rendered. Sections
 * inside it are parts of it, not stops of their own. Without a title it is no
 * stop and leaves the rail to the sections inside it.
 */
export const SettingsRailEntry = ({
  id,
  title,
  children,
}: Omit<SettingsRailItem, 'title'> & {
  title: string | undefined;
  children: ReactNode;
}) => {
  const registry = useContext(SettingsRailContext);
  useLayoutEffect(
    () => (title === undefined ? undefined : registry?.register({ id, title })),
    [registry, id, title]
  );
  return (
    <SettingsRailContext.Provider value={title === undefined ? registry : null}>
      {children}
    </SettingsRailContext.Provider>
  );
};
